/**
 * ClienteProdutosGrid - Sprint 3 (REDESIGN)
 *
 * Grid simples para cadastrar produtos DIRETAMENTE no cliente
 * Cada cliente tem seus próprios produtos
 * Campos: Código, Nome, Fabricante, Controlar por Lote
 * Layout: 77% Produtos / 23% GTINs
 */
import React, { useState, useEffect } from 'react';
import { Plus, Package, Trash2, RefreshCw, AlertCircle, Edit2, Save, X } from 'lucide-react';
import clienteService, { Cliente, ProdutoCliente } from '../services/clienteService';

interface ClienteProdutosGridProps {
  cliente: Cliente;
}

const ClienteProdutosGrid: React.FC<ClienteProdutosGridProps> = ({ cliente }) => {
  const [produtos, setProdutos] = useState<ProdutoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [formData, setFormData] = useState<ProdutoCliente>({
    codigo: '',
    descricao_completa: '',
    fabricante: '',
    controlar_validade: false
  });

  useEffect(() => {
    loadProdutos();
  }, [cliente.id]);

  const loadProdutos = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await clienteService.listProdutosCliente(cliente.id);
      setProdutos(data);
    } catch (err: any) {
      setError('Erro ao carregar produtos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId('new');
    setFormData({
      codigo: '',
      descricao_completa: '',
      fabricante: '',
      controlar_validade: false
    });
  };

  const handleEdit = (produto: ProdutoCliente) => {
    setEditingId(produto.id!);
    setFormData({ ...produto });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      codigo: '',
      descricao_completa: '',
      fabricante: '',
      controlar_validade: false
    });
  };

  const handleSave = async () => {
    if (!formData.codigo || !formData.descricao_completa) {
      setError('Código e Descrição são obrigatórios');
      return;
    }

    try {
      if (editingId === 'new') {
        await clienteService.createProdutoCliente(cliente.id, formData);
      } else {
        await clienteService.updateProdutoCliente(cliente.id, editingId as number, formData);
      }
      handleCancel();
      await loadProdutos();
    } catch (err: any) {
      setError('Erro ao salvar produto');
      console.error(err);
    }
  };

  const handleDelete = async (id: number, nome: string) => {
    if (!confirm(`Deseja remover o produto "${nome}"?`)) {
      return;
    }

    try {
      await clienteService.deleteProdutoCliente(cliente.id, id);
      await loadProdutos();
    } catch (err: any) {
      setError('Erro ao remover produto');
      console.error(err);
    }
  };

  const handleChange = (field: keyof ProdutoCliente, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Produtos do Cliente
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {produtos.length} produto(s) cadastrado(s)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadProdutos}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleAdd}
            disabled={editingId !== null}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            Novo Produto
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Layout 77/23 Split - Produtos e GTINs */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        alignItems: 'flex-start',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Coluna Esquerda: Tabela de Produtos (77%) */}
        <div style={{
          flex: '0 0 calc(77% - 0.5rem)',
          maxWidth: 'calc(77% - 0.5rem)',
          minWidth: 0,
          boxSizing: 'border-box'
        }}>
          {loading ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Carregando produtos...</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Código
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fabricante
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Controlar Lote
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {/* Linha de Novo Produto */}
                    {editingId === 'new' && (
                      <tr className="bg-blue-50 dark:bg-blue-900/20">
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={formData.codigo}
                            onChange={e => handleChange('codigo', e.target.value)}
                            placeholder="Código"
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                            autoFocus
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={formData.descricao_completa}
                            onChange={e => handleChange('descricao_completa', e.target.value)}
                            placeholder="Descrição do Produto"
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={formData.fabricante}
                            onChange={e => handleChange('fabricante', e.target.value)}
                            placeholder="Fabricante"
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={formData.controlar_validade}
                            onChange={e => handleChange('controlar_validade', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-right space-x-2">
                          <button
                            onClick={handleSave}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                          >
                            <Save className="w-4 h-4 inline" />
                          </button>
                          <button
                            onClick={handleCancel}
                            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                          >
                            <X className="w-4 h-4 inline" />
                          </button>
                        </td>
                      </tr>
                    )}

                    {/* Produtos Existentes */}
                    {produtos.length === 0 && editingId !== 'new' ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center">
                          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Nenhum produto cadastrado para este cliente
                          </p>
                          <button
                            onClick={handleAdd}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Cadastrar Primeiro Produto
                          </button>
                        </td>
                      </tr>
                    ) : (
                      produtos.map(produto => (
                        <tr key={produto.id} className={editingId === produto.id ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}>
                          {editingId === produto.id ? (
                            <>
                              <td className="px-4 py-3">
                                <input
                                  type="text"
                                  value={formData.codigo}
                                  onChange={e => handleChange('codigo', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="text"
                                  value={formData.descricao_completa}
                                  onChange={e => handleChange('descricao_completa', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="text"
                                  value={formData.fabricante}
                                  onChange={e => handleChange('fabricante', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                                />
                              </td>
                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={formData.controlar_validade}
                                  onChange={e => handleChange('controlar_validade', e.target.checked)}
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                />
                              </td>
                              <td className="px-4 py-3 text-right space-x-2">
                                <button
                                  onClick={handleSave}
                                  className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                >
                                  <Save className="w-4 h-4 inline" />
                                </button>
                                <button
                                  onClick={handleCancel}
                                  className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                                >
                                  <X className="w-4 h-4 inline" />
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {produto.codigo}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-900 dark:text-white">
                                  {produto.descricao_completa}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  {produto.fabricante || '-'}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  produto.controlar_validade
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                  {produto.controlar_validade ? 'Sim' : 'Não'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right space-x-2">
                                <button
                                  onClick={() => handleEdit(produto)}
                                  disabled={editingId !== null}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                                >
                                  <Edit2 className="w-4 h-4 inline" />
                                </button>
                                <button
                                  onClick={() => handleDelete(produto.id!, produto.descricao_completa)}
                                  disabled={editingId !== null}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                                >
                                  <Trash2 className="w-4 h-4 inline" />
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Coluna Direita: GTINs do Produto (23%) */}
        <div style={{
          flex: '0 0 calc(23% - 0.5rem)',
          maxWidth: 'calc(23% - 0.5rem)',
          minWidth: 0,
          boxSizing: 'border-box'
        }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              GTINs do Produto
            </h4>
            <div className="space-y-2">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Selecione um produto da tabela para visualizar e gerenciar seus GTINs.
              </p>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                <p className="text-xs text-gray-500 dark:text-gray-500 italic">
                  Funcionalidade em desenvolvimento
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClienteProdutosGrid;
